import { useEffect, useMemo, useState } from 'react';
import { createAppointment, fetchPractitionerSlots } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import type { Practitioner } from '@/mocks/practitioners';

interface BookingModalProps {
  practitioner: Practitioner;
  onClose: () => void;
}

interface DayOption {
  key: string;
  label: string;
  sublabel: string;
}

interface SlotRow {
  id: string;
  start_time: string;
  end_time: string;
}

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTH_LABELS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

function buildNextDays(count = 7): DayOption[] {
  const days: DayOption[] = [];
  const today = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const jsDow = d.getDay();
    days.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      label: i === 0 ? "Aujourd'hui" : i === 1 ? 'Demain' : DAY_LABELS[jsDow],
      sublabel: `${d.getDate()} ${MONTH_LABELS[d.getMonth()]}`,
    });
  }
  return days;
}

function formatTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  return `${String(h).padStart(2, '0')}h${String(m).padStart(2, '0')}`;
}

function generateToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function buildValidationUrl(token: string): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/rdv/valider?token=${token}`;
}

export default function BookingModal({ practitioner, onClose }: BookingModalProps) {
  const [days, setDays] = useState<DayOption[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<SlotRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [validationToken, setValidationToken] = useState('');

  const daysList = useMemo(() => buildNextDays(7), []);

  useEffect(() => {
    setDays(daysList);
    if (daysList.length > 0) setSelectedDay(daysList[0].key);
  }, [daysList]);

  useEffect(() => {
    if (!selectedDay) return;
    let cancelled = false;
    setLoading(true);
    setSlots([]);
    setSelectedSlot(null);
    setLoadError('');

    void fetchPractitionerSlots(practitioner.id, selectedDay)
      .then((result) => {
        if (cancelled) return;
        setSlots(result.slots);
        setSelectedSlot(result.slots[0] ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Impossible de charger les disponibilités.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [practitioner.id, selectedDay]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const newToken = generateToken();
      const appointment = await createAppointment({
        practitioner_id: Number(practitioner.id),
        patient_name: name.trim(),
        patient_phone: phone.trim(),
        appointment_date: selectedDay,
        appointment_time: selectedSlot.start_time,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        status: 'pending',
        validation_token: newToken,
      });

      setValidationToken(String(appointment.validation_token ?? newToken));
      trackEvent('appointment_booked', {
        specialty: practitioner.type,
        district: practitioner.district,
        subscription_tier: practitioner.subscriptionTier ?? '',
      });
      setDone(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "La réservation n'a pas abouti");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = name.trim().length > 0 && phone.trim().length >= 8 && selectedSlot;

  const bookedDay = daysList.find((d) => d.key === selectedDay);
  const validationUrl = validationToken ? buildValidationUrl(validationToken) : '';
  const waLink =
    practitioner.whatsapp && bookedDay && selectedSlot && validationUrl
      ? `https://wa.me/${practitioner.whatsapp}?text=${encodeURIComponent(
          `Bonjour ${practitioner.name}, je viens de réserver un rendez-vous via MedicaClic le ${bookedDay.label} ${bookedDay.sublabel} à ${formatTime(selectedSlot.start_time)}. Je m'appelle ${name}. Vous pouvez valider ou refuser cette demande en un clic ici : ${validationUrl}`,
        )}`
      : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/60" onClick={onClose} />
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full p-6 md:p-8 animate-fade-up max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 cursor-pointer"
        >
          <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
        </button>

        <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mb-5 ${practitioner.color}`}>
          <span className="font-heading font-bold text-lg">{practitioner.initials}</span>
        </div>

        {done ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-5">
              <i className="ri-time-line text-3xl text-secondary-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Demande de rendez-vous envoyée
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              {practitioner.name} a bien reçu votre demande pour le{' '}
              <strong className="text-foreground-900">
                {selectedSlot && selectedDay
                  ? `${daysList.find((d) => d.key === selectedDay)?.sublabel} à ${formatTime(selectedSlot.start_time)}`
                  : ''}
              </strong>.
              <br />
              Votre rendez-vous reste <strong className="text-foreground-900">en attente</strong> jusqu'à la confirmation du praticien.
              <br />
              Vous pouvez aussi le prévenir via WhatsApp pour accélérer la validation.
            </p>
            {waLink && (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap mb-3"
              >
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center" />
                Envoyer au praticien sur WhatsApp
              </a>
            )}
            {waLink && (
              <p className="text-xs text-foreground-500 leading-relaxed mb-3">
                Le message contient un lien sécurisé : le praticien confirme ou refuse le rendez-vous
                en un seul clic.
              </p>
            )}
            <button
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              Terminer
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-1">
              Prendre rendez-vous
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              {practitioner.name} · {practitioner.type} · {practitioner.district}
            </p>

            <p className="text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-2">Choisissez un jour</p>
            <div className="flex gap-2 overflow-x-auto pb-3 mb-5">
              {days.map((d) => (
                <button
                  key={d.key}
                  onClick={() => setSelectedDay(d.key)}
                  className={`flex flex-col items-center px-4 py-2.5 rounded-lg border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    selectedDay === d.key
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-background-300 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  <span className="font-medium">{d.label}</span>
                  <span className="text-xs text-foreground-500">{d.sublabel}</span>
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="w-7 h-7 flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
              </div>
            ) : loadError ? (
              <p className="text-sm text-accent-600 py-6 text-center">{loadError}</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-foreground-500 text-center py-6">
                Aucun créneau disponible ce jour. Essayez un autre jour.
              </p>
            ) : (
              <>
                <p className="text-xs font-semibold text-foreground-700 uppercase tracking-wide mb-2">Choisissez un horaire</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-6">
                  {slots.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedSlot(s)}
                      className={`px-2 py-2.5 rounded-md border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        selectedSlot?.id === s.id
                          ? 'border-primary-500 bg-primary-500 text-background-50 font-semibold'
                          : 'border-background-300 text-foreground-700 hover:border-primary-400'
                      }`}
                    >
                      {formatTime(s.start_time)}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Nom complet</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex. Amine Benali"
                  className="w-full px-3.5 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:outline-none text-sm text-foreground-800"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5 uppercase tracking-wide">Téléphone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Ex. 06 12 34 56 78"
                  className="w-full px-3.5 py-3 rounded-md border border-background-300 focus:border-primary-500 focus:outline-none text-sm text-foreground-800"
                />
              </div>
            </div>

            {submitError && (
              <p className="text-sm text-accent-600 mb-4">{submitError}</p>
            )}

            <button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-background-50 border-t-transparent animate-spin" />
                  Réservation en cours…
                </>
              ) : (
                <>
                  <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center" />
                  Confirmer le rendez-vous
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}